import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type SsoProtocol = 'saml' | 'oidc';

export interface SsoConnection {
  id: string;
  protocol: SsoProtocol;
  enabled: boolean;
  emailDomain: string | null;
  defaultRole: 'admin' | 'manager' | 'member' | 'employee';
  samlEntryPoint: string | null;
  samlIdpCertConfigured: boolean;
  oidcIssuerUrl: string | null;
  oidcClientId: string | null;
  oidcClientSecretConfigured: boolean;
  loginPath: string;
  metadataPath: string | null;
  updatedAt: string;
}

export interface UpsertSsoArgs {
  protocol: SsoProtocol;
  enabled: boolean;
  emailDomain?: string;
  defaultRole?: SsoConnection['defaultRole'];
  samlEntryPoint?: string;
  samlIdpCert?: string;
  oidcIssuerUrl?: string;
  oidcClientId?: string;
  oidcClientSecret?: string;
}

export function useSsoConnections() {
  return useQuery({
    queryKey: ['sso-connections'],
    queryFn: async () => {
      const r = await api.get<{ connections: SsoConnection[] }>('/api/sso/connections');
      return r.data.connections;
    },
  });
}

export function useUpsertSso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: UpsertSsoArgs) => {
      const r = await api.post<SsoConnection>('/api/sso/connections', args);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sso-connections'] }),
  });
}

export function useDeleteSso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/sso/connections/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sso-connections'] }),
  });
}

/** Absolute URL for an SSO login/metadata path (prefixes the API origin). */
export function ssoAbsoluteUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined) || '';
  return `${base}${path}`;
}
